import { useTranslation } from 'react-i18next';
import { Trash2, Save, Loader2 } from 'lucide-react';
import { Lesson } from '../../types';
import { VideoInput } from '../media';

interface LessonEditorProps {
  lesson: Lesson;
  onChange: (patch: Partial<Lesson>) => void;
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
  dirty: boolean;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold';

export default function LessonEditor({ lesson, onChange, onSave, onDelete, saving, dirty }: LessonEditorProps) {
  const { t } = useTranslation('teaching');

  return (
    <div className="glass-card rounded-lg p-5 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          {t('courseEdit.editLesson')}
          {dirty && <span className="w-2 h-2 rounded-full bg-amber-500" title={t('courseEdit.lessonUnsaved')} />}
        </h2>
        <button type="button" onClick={onDelete} className="inline-flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded">
          <Trash2 className="w-4 h-4" />{t('courseEdit.delete')}
        </button>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); onSave(); }}
        className="space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">{t('courseEdit.lessonTitle')}</label>
          <input type="text" value={lesson.title} onChange={(e) => onChange({ title: e.target.value })} className={inputCls} required />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">{t('courseEdit.content')}</label>
          <textarea value={lesson.content} onChange={(e) => onChange({ content: e.target.value })} rows={6} className={inputCls} placeholder={t('courseEdit.contentPlaceholder')} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">{t('courseEdit.lessonVideo')}</label>
          <VideoInput
            videoUrl={lesson.videoUrl}
            videoId={lesson.videoId}
            videoStatus={lesson.videoStatus}
            onChange={(videoUrl, videoId, videoStatus) => onChange({ videoUrl, videoId, videoStatus })}
            targetType="lesson"
            targetId={lesson._id}
            onCoverSelect={(coverUrl) => onChange({ thumbnailUrl: coverUrl })}
          />
        </div>

        <div className="max-w-[12rem]">
          <label className="block text-sm font-medium text-gray-600 mb-2">{t('courseEdit.duration')}</label>
          <input type="text" value={lesson.duration} onChange={(e) => onChange({ duration: e.target.value })} className={inputCls} placeholder={t('courseEdit.durationPlaceholder')} />
        </div>

        <div className="pt-2">
          <button type="submit" disabled={saving || !dirty} className="inline-flex items-center gap-1.5 btn-gold py-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('courseEdit.saveLessonChanges')}
          </button>
        </div>
      </form>
    </div>
  );
}
